import type { JSX } from 'react';

interface Props {
  readonly collapsed: boolean;
  readonly onToggle: () => void;
}

/**
 * A small icon button at the bottom of the sidebar, so the control sits on the
 * thing it controls rather than in the header.
 *
 * One button in two states rather than two buttons: focus is not lost when it
 * flips, which matters for anyone driving this from the keyboard.
 *
 * No visible text -- the chevron's direction carries the meaning, and the
 * accessible name states the action for anyone who cannot see it.
 */
export function SidebarToggle({ collapsed, onToggle }: Props): JSX.Element {
  const label = collapsed ? 'Show patient list' : 'Hide patient list';

  return (
    <button
      type="button"
      className="sidetoggle"
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-controls="patient-list"
      // States the action, not the state -- a screen reader user is told what
      // pressing it will do.
      aria-label={label}
      title={label}
    >
      <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true">
        <polyline
          points={collapsed ? '5,2 10,7 5,12' : '9,2 4,7 9,12'}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
